import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { generateShareCard, buildShareText, ShareCardData } from '../utils/generateShareCard';
import './ShareProgressModal.css';

import { Picto, EmojiText } from './common/Picto';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: ShareCardData;
}

type ShareState = 'idle' | 'generating' | 'done' | 'error';

const ShareProgressModal: React.FC<Props> = ({ isOpen, onClose, data }) => {
  const { t } = useTranslation();
  const [shareState, setShareState] = useState<ShareState>('idle');
  const [imageUrl, setImageUrl]     = useState<string | null>(null);
  const [copied, setCopied]         = useState(false);
  const blobRef = useRef<Blob | null>(null);

  // Pré-génère l'image dès l'ouverture
  useEffect(() => {
    if (!isOpen) return;
    setShareState('generating');
    setImageUrl(null);
    blobRef.current = null;

    generateShareCard(data)
      .then(blob => {
        blobRef.current = blob;
        setImageUrl(URL.createObjectURL(blob));
        setShareState('done');
      })
      .catch(() => setShareState('error'));

    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // WhatsApp
  const handleWhatsApp = () => {
    const text = encodeURIComponent(buildShareText(data));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Web Share API (mobile natif — partage vers Instagram, etc.)
  const handleNativeShare = async () => {
    const shareText = buildShareText(data);

    // Essaie de partager avec l'image
    if (blobRef.current && navigator.canShare) {
      const file = new File([blobRef.current], 'jogolinga-progression.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: shareText, title: 'Jogolinga' });
          return;
        } catch { /* annulé par l'utilisateur */ }
      }
    }

    // Fallback — partage sans image
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url: 'https://jogolinga.com', title: 'Jogolinga' });
      } catch {}
    }
  };

  // Télécharger l'image
  const handleDownload = () => {
    if (!blobRef.current) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blobRef.current);
    a.download = `jogolinga-${data.languageName.toLowerCase()}-${Date.now()}.png`;
    a.click();
  };

  // Copier le texte
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText(data));
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  };

  const supportsNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

  return ReactDOM.createPortal(
   <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="spm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="spm-panel"
            initial={{ y: 60, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          >
            {/* En-tête */}
            <div className="spm-header">
              <h2 className="spm-title">{t('shareProgress.title')}</h2>
              <button className="spm-close" onClick={onClose} aria-label={t('shareProgress.close')}>✕</button>
            </div>

            {/* Aperçu carte */}
            <div className="spm-preview">
              {shareState === 'generating' && (
                <div className="spm-preview__loading">
                  <div className="spm-spinner" />
                  <span>{t('shareProgress.generating')}</span>
                </div>
              )}
              {shareState === 'done' && imageUrl && (
                <img src={imageUrl} alt={t('shareProgress.title')} className="spm-preview__img" />
              )}
              {shareState === 'error' && (
                <div className="spm-preview__error">
                  <EmojiText text={t('shareProgress.generateError') as string} />
                </div>
              )}
            </div>

            {/* Stats mini-résumé */}
            <div className="spm-stats">
              <div className="spm-stat">
                <span className="spm-stat__val">{data.wordsLearned}</span>
                <span className="spm-stat__lbl">{t('shareProgress.wordsLearned')}</span>
              </div>
              <div className="spm-stat-sep" />
              <div className="spm-stat">
                <span className="spm-stat__val"><Picto name="feu" /> {data.streakDays}</span>
                <span className="spm-stat__lbl">{t('shareProgress.days')}</span>
              </div>
              <div className="spm-stat-sep" />
              <div className="spm-stat">
                <span className="spm-stat__val">+{data.xpGained}</span>
                <span className="spm-stat__lbl">XP</span>
              </div>
            </div>

            {/* Boutons de partage */}
            <div className="spm-actions">
              <button className="spm-btn spm-btn--whatsapp" onClick={handleWhatsApp}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t('shareProgress.whatsapp')}
              </button>

              {supportsNativeShare && (
                <button className="spm-btn spm-btn--native" onClick={handleNativeShare}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
                  </svg>
                  {t('shareProgress.share')}
                </button>
              )}

              <button
                className="spm-btn spm-btn--download"
                onClick={handleDownload}
                disabled={shareState !== 'done'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
                {t('shareProgress.image')}
              </button>

              <button className="spm-btn spm-btn--copy" onClick={handleCopy}>
                {copied
                  ? <><span>✓</span> {t('shareProgress.copied')}</>
                  : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>{t('shareProgress.copy')}</>
                }
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ShareProgressModal;
